import {AfterContentChecked, Component, Input} from '@angular/core';
import {RouterService} from '@npck/libs/commons';
import {Observable} from 'rxjs';

@Component({
    selector: 'ui-kit-breadcrumbs',
    templateUrl: 'breadcrumbs.component.html',
    standalone: false,
})
export class BreadcrumbsComponent implements AfterContentChecked {
    @Input() filterOneLevelNavigation: boolean;
    @Input() customLastBreadcrumb: string;
    @Input() filterNavigationPath: string | string[];
    @Input() color: 'gray' | 'green' = 'gray';
    @Input() prefixPaths: string[] = [];
    @Input() rootPath: string;
    @Input() tabPathToTab: Record<string, string>;
    @Input() specialFilter: (path: string) => boolean;
    @Input() navigateBackListener: () => Observable<boolean>;
    @Input() customNavigation: (path: string) => boolean | void;
    @Input() breadcrumbCustomizer: (path: string) => string;
    @Input() replaceUrl = false;
    @Input() passQueryParams = false;

    breadcrumbs: string[];
    multiPathPrefix: string[];
    customizedBreadcrumbs: Record<string, string> = {};
    private uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/i;

    constructor(
        private routerService: RouterService,
    ) {
    }

    ngAfterContentChecked(): void {
        this.multiPathPrefix = this.prefixPaths.find(prefix => this.routerService.getCurrentUrl().startsWith(`/${prefix}`))
            ?.split('?')[0]
            ?.split('/');
        this.breadcrumbs = this.getOriginalBreadcrumbs();
        this.customizedBreadcrumbs = {};

        if (this.filterNavigationPath) {
            this.breadcrumbs = this.breadcrumbs.filter(path => {
                if (Array.isArray(this.filterNavigationPath)) {
                    return !this.filterNavigationPath.includes(path);
                }
                return path !== this.filterNavigationPath;
            });
        }

        if (this.filterOneLevelNavigation) {
            this.breadcrumbs = this.breadcrumbs.filter((_, index) => index !== this.breadcrumbs.length - 2);
        }

        if (this.customLastBreadcrumb || this.breadcrumbCustomizer) {
            const hasIdOrSpecialFilterPath = this.breadcrumbs.some(path => this.checkPathIsIdOrUuidOrSpecialFilterPath(path));
            if (hasIdOrSpecialFilterPath) {
                this.breadcrumbs = this.breadcrumbs
                    .map(path => {
                        if (this.checkPathIsIdOrUuidOrSpecialFilterPath(path)) {
                            this.customizedBreadcrumbs[path] = this.breadcrumbCustomizer
                                ? this.breadcrumbCustomizer(path)
                                : this.customLastBreadcrumb;
                        }
                        return path;
                    });
            } else if (this.customLastBreadcrumb) {
                this.breadcrumbs.push(this.customLastBreadcrumb);
            }
        } else {
            this.breadcrumbs = this.breadcrumbs.filter(path => !this.checkPathIsIdOrUuidOrSpecialFilterPath(path));
        }
    }

    public navigateBack(): void {
        this.navigate(this.breadcrumbs.length - 1);
    }

    public navigateBackSkipListener(): void {
        this.navigateToPath(this.breadcrumbs.length - 1);
    }

    navigateTo(index: number): void {
        this.navigate(index + 1);
    }

    private checkPathIsIdOrUuidOrSpecialFilterPath(path: string): boolean {
        return this.uuidRegex.test(path) || !isNaN(Number(path)) || (this.specialFilter && this.specialFilter(path));
    }

    private navigate(index: number): void {
        if (this.navigateBackListener) {
            this.navigateBackListener().subscribe(result => result && this.navigateToPath(index));
        } else {
            this.navigateToPath(index);
        }
    }

    private navigateToPath(index: number): void {
        let breadcrumbs: string[];
        if (this.customLastBreadcrumb && this.breadcrumbs[index - 1] === this.customLastBreadcrumb || this.customizedBreadcrumbs) {
            breadcrumbs = this.getOriginalBreadcrumbs().slice(0, index);
        } else {
            breadcrumbs = this.breadcrumbs.slice(0, index);
        }

        if (breadcrumbs[index - 1] === this.rootPath) {
            breadcrumbs = breadcrumbs.filter(path => !this.multiPathPrefix?.includes(path));
        }

        if (this.customNavigation && breadcrumbs.length) {
            const lastBreadCrumb = breadcrumbs[breadcrumbs.length - 1];
            const navUnhandled = this.customNavigation(lastBreadCrumb);
            if (!navUnhandled) {
                return;
            }
        }

        if (this.tabPathToTab && breadcrumbs.length) {
            const lastBreadCrumb = breadcrumbs[breadcrumbs.length - 1];
            const tab = this.tabPathToTab[lastBreadCrumb];
            if (tab) {
                breadcrumbs = breadcrumbs.filter(path => path !== lastBreadCrumb);
                const path = breadcrumbs.join('/');
                this.routerService.navigateToTab<string>(path, tab, {replaceUrl: this.replaceUrl, passQueryParams: this.passQueryParams});
                return;
            }
        }

        const path = breadcrumbs.join('/');
        this.routerService.navigate(path, {replaceUrl: this.replaceUrl, passQueryParams: this.passQueryParams});
    }

    private getOriginalBreadcrumbs(): string[] {
        return this.routerService.getCurrentUrl()
            .split('?')[0]
            .split('/')
            .filter(path => !!path);
    }
}
